import type { CustomSEO } from '@iheartjane/data-access';
import { useFormContext } from '@iheartjane/reefer-hook-form';
import { useContextEnsuringProvider } from '@iheartjane/utils';
import type { ReactNode } from 'react';
import { createContext, useCallback, useRef, useState } from 'react';
import type { Path, PathValue } from 'react-hook-form';

type FieldName = string;

interface SeoFormContextType {
  insertVariable: (fieldName: FieldName, variable: string) => void;
  setupEventListeners: (cursorTrackingFields: FieldName[]) => () => void;
}

const SeoFormContext = createContext<SeoFormContextType | null>(null);
SeoFormContext.displayName = 'SeoForm';

export const useSeoForm = () => useContextEnsuringProvider(SeoFormContext);

export const SeoFormProvider = ({ children }: { children: ReactNode }) => {
  const [cursorPositions, setCursorPositions] = useState<
    Record<FieldName, number>
  >({});
  const formRef = useRef<HTMLDivElement>(null);
  const { setValue } = useFormContext<CustomSEO>();

  const trackCursor = useCallback((fieldName: FieldName, position: number) => {
    setCursorPositions((prev) => ({ ...prev, [fieldName]: position }));
  }, []);

  const getInputElement = useCallback(
    (fieldName: FieldName): HTMLInputElement | HTMLTextAreaElement | null => {
      if (!formRef.current) return null;

      const inputElement = formRef.current.querySelector(
        `input[name="${fieldName}"]`
      ) as HTMLInputElement;
      const textareaElement = formRef.current.querySelector(
        `textarea[name="${fieldName}"]`
      ) as HTMLTextAreaElement;

      return inputElement || textareaElement || null;
    },
    []
  );

  const attachEventListeners = useCallback(
    (cursorTrackingFields: FieldName[]) => {
      const cleanupFunctions: (() => void)[] = [];

      cursorTrackingFields.forEach((fieldName) => {
        const element = getInputElement(fieldName);

        if (element) {
          const handler = () =>
            trackCursor(fieldName, element.selectionStart ?? 0);
          ['focus', 'click', 'keyup'].forEach((eventType) => {
            element.addEventListener(eventType, handler);
          });

          cleanupFunctions.push(() => {
            ['focus', 'click', 'keyup'].forEach((eventType) => {
              element.removeEventListener(eventType, handler);
            });
          });
        }
      });

      return cleanupFunctions;
    },
    [getInputElement, trackCursor]
  );

  const setupEventListeners = useCallback(
    (cursorTrackingFields: FieldName[]) => {
      // Try to attach listeners immediately
      let cleanupFunctions = attachEventListeners(cursorTrackingFields);

      // If elements weren't found, retry after a small delay
      if (cleanupFunctions.length === 0) {
        const timeoutId = setTimeout(() => {
          cleanupFunctions = attachEventListeners(cursorTrackingFields);
        }, 100);

        return () => {
          clearTimeout(timeoutId);
          cleanupFunctions.forEach((cleanup) => cleanup());
        };
      }

      return () => {
        cleanupFunctions.forEach((cleanup) => cleanup());
      };
    },
    [attachEventListeners]
  );

  const updateField = <TField extends Path<CustomSEO>>(
    field: TField,
    value: PathValue<CustomSEO, TField>
  ) => {
    setValue(field, value);
  };

  const insertVariable = useCallback(
    (fieldName: FieldName, variable: string) => {
      const element = getInputElement(fieldName);
      if (!element) return;

      const currentValue = element.value;
      // Append when the field has never been focused or its value has since shrunk
      const position = Math.min(
        cursorPositions[fieldName] ?? currentValue.length,
        currentValue.length
      );
      const variableText = `{{ ${variable} }}`;

      const newValue =
        currentValue.slice(0, position) +
        variableText +
        currentValue.slice(position);

      const newCursorPosition = position + variableText.length;
      const isInsertingAtEnd = position === currentValue.length;

      // Update both the DOM element and the form state
      element.value = newValue;
      element.dispatchEvent(new Event('input', { bubbles: true }));

      updateField(
        fieldName as Path<CustomSEO>,
        newValue as PathValue<CustomSEO, Path<CustomSEO>>
      );

      // If inserting at the end, cursor will end up in the correct position
      if (isInsertingAtEnd) {
        element.focus();
        trackCursor(fieldName, newCursorPosition);
        return;
      }

      // For mid-text insertions, use retry logic to ensure cursor position
      const setCursorWithRetry = (attempt = 0) => {
        if (!element || attempt > 5) return;

        element.focus();
        element.setSelectionRange(newCursorPosition, newCursorPosition);
        trackCursor(fieldName, newCursorPosition);

        setTimeout(
          () => {
            if (element.selectionStart !== newCursorPosition) {
              setCursorWithRetry(attempt + 1);
            }
          },
          10 + attempt * 10
        );
      };

      setTimeout(() => setCursorWithRetry(), 0);
    },
    [cursorPositions, getInputElement, trackCursor]
  );

  return (
    <SeoFormContext.Provider
      value={{
        insertVariable,
        setupEventListeners,
      }}
    >
      <div ref={formRef}>{children}</div>
    </SeoFormContext.Provider>
  );
};
